import { LoginForm } from './auth/LoginForm';
import { useLogout, useMe } from './auth/useAuth';
import { TripProvider, useTripContext } from './trips/TripContext';
import { TripSwitcher } from './trips/TripSwitcher';
import { CountryTabs } from './countries/CountryTabs';
import { Board } from './lists/Board';
import { MapProvider } from './map/MapContext';
import { MapView } from './map/MapView';
import { Itinerary } from './itinerary/Itinerary';
import { IdeaInbox } from './ideas/IdeaInbox';
import { DashboardHome } from './dashboard/DashboardHome';
import { SeedTray } from './seed/SeedTray';

export function App() {
  const me = useMe();

  if (me.isLoading) {
    return <p className="text-secondary text-center my-5 py-5">Loading…</p>;
  }

  if (!me.data) return <LoginForm />;

  return (
    <TripProvider>
      <Dashboard displayName={me.data.displayName} />
    </TripProvider>
  );
}

function Dashboard({ displayName }: { displayName: string }) {
  const logout = useLogout();
  const { selectedTrip, selectedCountry, isLoading } = useTripContext();

  return (
    <>
      <header
        className="glass sticky-top"
        style={{ borderWidth: '0 0 1px', borderRadius: 0 }}
      >
        <div
          className="d-flex justify-content-between align-items-center gap-3 flex-wrap mx-auto px-3 py-2"
          style={{ maxWidth: 980 }}
        >
          <h1 className="h4 m-0 d-flex align-items-center gap-2">
            <span aria-hidden>✈️</span> Travel Planner
          </h1>
          <div className="d-flex gap-3 align-items-center">
            <span className="text-secondary small">{displayName}</span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto px-3 pb-5" style={{ maxWidth: 980, lineHeight: 1.5 }}>
        <div className="mt-4">
          <TripSwitcher />
        </div>

        {isLoading ? (
          <p className="text-secondary mt-3">Loading trips…</p>
        ) : !selectedTrip ? (
          <p className="text-secondary mt-4">No trips yet — create one above to get started.</p>
        ) : (
          <>
            <p className="text-secondary mt-2 mb-0">
              {selectedTrip.start} → {selectedTrip.end}
              {selectedTrip.tripType ? ` · ${selectedTrip.tripType}` : ''}
            </p>
            <DashboardHome tripId={selectedTrip.id} />
            <IdeaInbox tripId={selectedTrip.id} />
            <MapProvider tripId={selectedTrip.id}>
              <CountryTabs />
              <section className="mt-4">
                {selectedCountry ? (
                  <Board
                    key={selectedCountry.id}
                    tripId={selectedTrip.id}
                    countryId={selectedCountry.id}
                  />
                ) : (
                  <p className="text-secondary">Add a country to start planning.</p>
                )}
              </section>
              <MapView
                tripId={selectedTrip.id}
                countryId={selectedCountry?.id ?? null}
                countryName={selectedCountry?.name ?? null}
              />
              {selectedCountry && (
                <section className="mt-4">
                  <SeedTray tripId={selectedTrip.id} countryId={selectedCountry.id} />
                </section>
              )}
              <section className="mt-4">
                <Itinerary tripId={selectedTrip.id} />
              </section>
            </MapProvider>
          </>
        )}
      </main>
    </>
  );
}
